'use client';

import React, { useEffect, useState } from 'react';
import { DataTable } from '@/components/(postTypes)/platforms/(single)/(exchanges-props)/iranian/DataTable';
import { useColumns } from '@/components/(postTypes)/platforms/(single)/(exchanges-props)/iranian/coinsColumns';
import useExchangeList from '@/utils/coins/exchangesList/fetchCoins';
import useStoreID from '@/stores/exchangesNumber/store';
import { CoinData, ExchangeCoins } from '@/types/exchanges/single';

interface SingleCoinExchangesListProps {
    symbol: string;
    type: number;
    initialExchangeList?: ExchangeCoins | null;
    page: number;
    coins?: CoinData;
    title?: string;
}

interface SortOption {
    value: string;
    label: string;
}

const sortOptions: SortOption[] = [
    { value: 'priceBuyUSDT', label: 'بهترین قیمت خرید از شما' },
    { value: 'amountBuyUSDT', label: 'بیشترین مقدار خرید از شما' },
    { value: 'priceSellUSDT', label: 'بهترین قیمت فروش به شما' },
    { value: 'amountSellUSDT', label: 'بیشترین مقدار فروش به شما' },
];

const SingleExchangeCoinsList: React.FC<SingleCoinExchangesListProps> = ({
    symbol,
    type,
    initialExchangeList,
    title,
}) => {
    const {
        exchangeList,
        currentPage,
        pageSize,
        totalPages,
        setCurrentPage,
        setPageSize,
        fetchExchanges,
    } = useExchangeList(symbol);

    const { setNumbers } = useStoreID.getState();

    useEffect(() => {
        setNumbers(initialExchangeList?.paginate_data?.total_items || 0);
    }, [initialExchangeList?.paginate_data?.total_items, setNumbers, totalPages]);

    const [currency, setCurrency] = useState<string>('TMN');
    const [sorting, setSorting] = useState([{ id: 'priceBuyUSDT', desc: true }]);
    const [selectedSort, setSelectedSort] = useState<string>(sortOptions[0].value);

    const handleSortChange = (newValue: string) => {
        setSelectedSort(newValue);
        setSorting((prev) => {
            const currentSort = prev.find((s) => s.id === newValue);
            const isDescending = currentSort ? currentSort.desc : false;
            return [{ id: newValue, desc: !isDescending }];
        });
    };

    const handleCurrencyChange = (selectedCurrency: string) => {
        setCurrency(selectedCurrency);
        setSorting([
            {
                id: selectedCurrency === 'USDT' ? 'priceSellUSDT' : 'priceBuyUSDT',
                desc: true,
            },
        ]);
    };
    const handlePaginationAction = (page: number) => {
        setCurrentPage(page);
        fetchExchanges(page); // Fetch more data when pagination changes
    };

    const coins = initialExchangeList?.exchange?.coinData;
    const paginationProps = initialExchangeList?.paginate_data;
    const dataToPass: any = exchangeList.length > 0 ? exchangeList : coins;
    const registerLink = initialExchangeList?.exchange?.registerLink;
    const columns = useColumns({
        currency,
        selectedSort,
        dataToPass,
        coins,
        registerLink,
        handleSortChange,
        currentPage, // Pass currentPage here
    });

    // When there is no data to display
    if (!dataToPass || !Array.isArray(dataToPass) || dataToPass.length === 0) {
        return null;
    }

    return (
        <section className='w-full overflow-auto lg:px-3 my-5 pb-5 lg:pb-10 border-b dark:border-white/20'>
            <h2 className='text-xl font-bold mb-5'>لیست ارزهای {title}</h2>
            <div className='flex w-full mb-3 items-center max-lg:flex-col max-lg:justify-between gap-2'>
                <div className='flex gap-3 max-lg:w-full max-lg:justify-between'>
                    {type !== 2 && (
                        <div className='flex w-full justify-between gap-2 rounded-lg dark:bg-white/10 bg-bg p-1 max-lg-py-3'>
                            <button
                                className={`w-full flex-1 rounded-lg px-6 py-2 font-light ${
                                    currency === 'TMN'
                                        ? 'bg-white dark:bg-white text-primary dark:text-black'
                                        : 'text-primary/40 dark:text-white'
                                } text-xs`}
                                onClick={() => handleCurrencyChange('TMN')}
                            >
                                تومان
                            </button>
                            <button
                                className={`w-full flex-1 rounded-lg px-6 py-2 font-light ${
                                    currency === 'USDT'
                                        ? 'bg-white dark:bg-white text-primary dark:text-black'
                                        : 'text-primary/40 dark:text-white'
                                } text-xs`}
                                onClick={() => handleCurrencyChange('USDT')}
                            >
                                دلار
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className='block overflow-auto dark:border-white/10 border border-primary-dark/10 rounded-2xl'>
                <DataTable
                    handlePaginationAction={handlePaginationAction}
                    currentPage={currentPage}
                    totalPages={paginationProps?.total_items || totalPages}
                    handlePageSizeChangeAction={(size) => setPageSize(size)}
                    pageSize={paginationProps?.per_page || pageSize}
                    data={dataToPass} // If coinsList exists, use it; otherwise use exchangeList
                    columns={columns}
                    sorting={sorting}
                    setSorting={setSorting}
                />
            </div>
        </section>
    );
};

export default React.memo(SingleExchangeCoinsList);
